import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, message, Modal, Drawer, Button, Popconfirm } from 'antd';
import posthog from 'posthog-js';
import { InvoiceFormBase, createSupplierInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import InvoiceUploadandParse, {
	InvoiceLoadingSkeletonAnimated,
} from '../invoice_uploader/InvoiceUploadandParse';
import {
	invoicesRestCrudThunksForSupplier,
	updateInvoiceSubContractor,
	createInvoiceSubContractor,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
} from '../../thunks/invoices_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { getLinkWIthBackLinkParams, getLocationSearch } from '../../utils/HistoryUtils';
import {
	applyParsedInvoiceToForm,
	buildInvoicePayloadFromFormValues,
	compareParsedWithFinal,
	getAssetSuggestion,
	getCurrencyMismatch,
	getWorkOrderCheck,
	getWorkOrderCheckOutcome,
	shouldSurfaceWorkOrderCheck,
} from '../../utils/InvoiceUtils';
import { WorkOrderCheckModal } from '../invoice_work_order_check/WorkOrderCheckModal';
import { isInvoiceWorkOrderCheckEnabled } from '../../utils/FeatureFlagUtils';
import {
	getCurrency,
	getEntityById,
	isSubContractor,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import { isInternalTech } from '../../utils/AuthUtils';
import { getContractorWO } from '../../utils/WorkOrderUtils';
import { proposalsRestCrudThunksForSupplier } from '../../thunks/proposals_thunks';
import { BillToInfoBanner } from '../bill_to_info_banner/BillToInfoBanner';

const queryString = require('qs');
const { Content } = Layout;

const SupplierInvoiceAIForm: FC<any> = ({
	createInvoice,
	updateInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	userType,
	currentUser,
	companyConfig,
	workOrder,
	creating,
	updating,
	createErrors,
	updateErrors,
	createContractorInvoice,
	updateContractorInvoice,
	isClientView,
	getQuotes,
}): React.ReactElement => {
	const [invoiceFormData, setInvoiceFormData] = useState<any>({});
	const [invoiceFormDataLoading, setInvoiceFormDataLoading] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	// Bumped on user-initiated "start over" events (delete, reset, error,
	// currency mismatch). NOT bumped on parse-complete — remounting the
	// uploader mid-S3 would orphan the in-flight upload, and we'd lose the
	// final invoicePDFLink.
	const [uploaderKey, setUploaderKey] = useState(0);
	const [parsedDiscount, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	// The work order mismatch verdict the uploader acknowledged, kept so the form can keep showing
	// the evidence after the modal is gone. `pendingWorkOrderCheck` holds the parse that is waiting
	// on that acknowledgement — it is applied only if they choose to continue.
	const [acknowledgedWorkOrderCheck, setAcknowledgedWorkOrderCheck] = useState<any>(null);
	const [pendingWorkOrderCheck, setPendingWorkOrderCheck] = useState<any>(null);
	// What the invoice printed about the work order's linked asset. Only ever present on a
	// Consistent verdict, so it and a mismatch warning are mutually exclusive.
	const [assetSuggestion, setAssetSuggestion] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const [hasQuote, setHasQuote] = useState(false);
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);

	// Fires when the S3 upload completes. invoicePDFLink is only ever set from
	// here, so it's always a real URL — never the local base64 preview.
	const handleS3UrlReady = useCallback((s3Url: string) => {
		setInvoiceFormData((prev) => ({ ...prev, invoicePDFLink: s3Url }));
		setPdfUrl(s3Url);
	}, []);

	// Fires after S3 retries are exhausted. Clear any stale link from a prior
	// upload, reset the dragger so the user can pick a new file, and surface
	// the failure as a toast.
	const handleS3UploadError = useCallback(() => {
		message.error('Failed to upload invoice PDF. Please try uploading again.');
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
		setUploaderKey((prev) => prev + 1);
	}, []);

	const workOrderId = useMemo(() => {
		const searchString = getLocationSearch(location);
		const queryParams = queryString.parse(searchString);
		return queryParams.workOrderId || match.params.id;
	}, [location, match.params.id]);

	useEffect(() => {
		if (nullSafeGet('id', workOrder)) {
			getQuotes(
				{
					workOrderId: nullSafeGet('id', workOrder),
					buyerFacilityId: workOrder.buyerFacilityId,
				},
				'workOrderAssociatedQuotes'
			).then((proposals) => {
				if (proposals && proposals.length > 0) {
					setHasQuote(true);
				}
			});
		}
	}, []);
	const currency = useMemo(() => {
		if (hasQuote) {
			return getCurrency({ workOrder });
		}
		return getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
	}, [hasQuote, workOrder]);
	useEffect(() => {
		if (workOrderId) {
			setInvoiceFormData((prevFormData) => {
				if (prevFormData.workOrderId !== workOrderId) {
					return {
						...prevFormData,
						workOrderId: workOrderId,

						externalWorkOrderId: prevFormData.externalWorkOrderId || String(workOrderId),
						supplierFacilityId:
							workOrder && workOrder.supplierFacilityId
								? workOrder.supplierFacilityId
								: prevFormData.supplierFacilityId,
					};
				}

				if (!prevFormData.externalWorkOrderId) {
					return {
						...prevFormData,
						externalWorkOrderId: String(workOrderId),
					};
				}
				return prevFormData;
			});
		}
	}, [workOrderId, workOrder]);

	const handleDeletePdf = useCallback(() => {
		if (parsedFormDataSnapshot) {
			posthog.capture('invoice_pdf_deleted_after_parsing', {
				user_type: userType,
			});
		}
		setPdfUrl(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setAcknowledgedWorkOrderCheck(null);
		setPendingWorkOrderCheck(null);
		setAssetSuggestion(null);
		setParsingLoading(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		setShowDrawer(false);

		setInvoiceFormData((prevFormData) => {
			if (prevFormData) {
				return { ...prevFormData, invoicePDFLink: undefined };
			}
			return prevFormData;
		});
	}, [parsedFormDataSnapshot, userType]);

	const handleBackConfirm = useCallback(() => {
		handleDeletePdf();
	}, [handleDeletePdf]);

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setAcknowledgedWorkOrderCheck(null);
		setPendingWorkOrderCheck(null);
		setAssetSuggestion(null);
		setParsingLoading(false);
		setDisableCosts(false);

		// Reset form data to initial state with only workOrderId
		setInvoiceFormData({
			workOrderId: workOrderId,
			externalWorkOrderId: String(workOrderId),
			supplierFacilityId:
				workOrder && workOrder.supplierFacilityId ? workOrder.supplierFacilityId : undefined,
		});

		// Increment form key to force re-render
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);

		// Track reset event
		posthog.capture('invoice_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [workOrderId, workOrder, userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		// Show PDF immediately when file is selected
		if (fileData.fileUrl) {
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		} else {
			console.error('handleFileSelected: fileUrl is undefined');
		}
	}, []);

	const handleUploadError = useCallback((error = {}) => {
		message.error('Error parsing invoice. Please try again.');
		setParsingLoading(false);
		setShowDrawer(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		// Drop any link from a prior upload so a follow-up can't submit with a stale PDF.
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
		posthog.capture('invoice_pdf_parse_error', {
			user_type: userType,
			error: error.message,
		});
	}, []);

	const handleParsingStart = useCallback(() => {
		setParsingLoading(true);
	}, []);

	const handleParsingComplete = useCallback(() => {
		setParsingLoading(false);
	}, []);

	// Extracted so the work order mismatch modal can run the exact same apply on "continue" that
	// an unflagged parse runs immediately — the two paths must not drift.
	const applyParse = useCallback(
		(parsedData) => {
			setAssetSuggestion(getAssetSuggestion(parsedData));
			applyParsedInvoiceToForm({
				parsedData,
				workOrder,
				userType,
				setInvoiceFormData,
				setParsedFormDataSnapshot,
				setParsedDiscount,
				setDisableCosts,
			});
			setFormKey((prev) => prev + 1);
			setShowDrawer(true);
		},
		[workOrder, userType]
	);

	const handleUploadSuccess = useCallback(
		(data) => {
			if (!data.parsedData) {
				return;
			}

			const mismatch = getCurrencyMismatch(data.parsedData, currency);
			if (mismatch) {
				setPdfUrl(null);
				setParsingLoading(false);
				setShowDrawer(false);
				setFormKey((prev) => prev + 1);
				setUploaderKey((prev) => prev + 1);
				// Drop the wrong-currency PDF's link so it can't get submitted later.
				setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
				Modal.confirm({
					title: 'Currency Mismatch',
					content: `The uploaded invoice is in ${mismatch.parsedCurrency}, but your account is configured to use ${mismatch.systemCurrency}.`,
					okText: 'Create from Scratch',
					cancelText: 'Change Company Currency',
					onOk: () => {
						posthog.capture('invoice_pdf_currency_mismatch_create_from_scratch', {
							user_type: userType,
							parsed_currency: mismatch.parsedCurrency,
							system_currency: mismatch.systemCurrency,
						});
						history.push(
							`/supplier/workOrders/detail/${workOrderId}/invoices/${
								isInternalTech ? 'newForSupplier' : 'new'
							}`
						);
					},
					onCancel: () => {
						posthog.capture('invoice_pdf_currency_mismatch_change_currency', {
							user_type: userType,
							parsed_currency: mismatch.parsedCurrency,
							system_currency: mismatch.systemCurrency,
						});
						history.push('/supplier/company/me');
					},
					width: '50vw',
				});
				return;
			}

			// The backend's verdict on whether this invoice belongs to this work order. Ask before
			// applying the parse — a genuine wrong-work-order upload is cheaper to abandon than to
			// undo — but never block: continuing is always available.
			const workOrderCheck = getWorkOrderCheck(data.parsedData);
			if (shouldSurfaceWorkOrderCheck(workOrderCheck)) {
				setPendingWorkOrderCheck({ workOrderCheck, parsedData: data.parsedData });
				posthog.capture('invoice_work_order_mismatch_shown', {
					user_type: userType,
					work_order_id: workOrderId,
					outcome: getWorkOrderCheckOutcome(workOrderCheck),
				});
				return;
			}

			setAcknowledgedWorkOrderCheck(null);
			applyParse(data.parsedData);
		},
		[applyParse, userType, currency, history, workOrderId]
	);

	const handleWorkOrderCheckContinue = useCallback(() => {
		if (!pendingWorkOrderCheck) {
			return;
		}
		posthog.capture('invoice_work_order_mismatch_continued', {
			user_type: userType,
			work_order_id: workOrderId,
			outcome: getWorkOrderCheckOutcome(pendingWorkOrderCheck.workOrderCheck),
		});
		setAcknowledgedWorkOrderCheck(pendingWorkOrderCheck);
		applyParse(pendingWorkOrderCheck.parsedData);
		setPendingWorkOrderCheck(null);
	}, [pendingWorkOrderCheck, applyParse, userType, workOrderId]);

	const handleWorkOrderCheckCancel = useCallback(() => {
		posthog.capture('invoice_work_order_mismatch_cancelled', {
			user_type: userType,
			work_order_id: workOrderId,
		});
		setPendingWorkOrderCheck(null);
		handleDeletePdf();
	}, [handleDeletePdf, userType, workOrderId]);

	const onSuccess = useCallback(
		(rec) => {
			history.goBack();
		},
		[history]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const currentInvoicePDFLink = invoiceFormData && invoiceFormData.invoicePDFLink;
			if (!currentInvoicePDFLink) {
				message.error('Invoice PDF is still uploading. Please wait a moment and try again.');
				return;
			}

			const isUpdate = values.id ? true : false;
			const submitAction = values.id ? updateInvoice : createInvoice;
			const submitContractorAction = values.id ? updateContractorInvoice : createContractorInvoice;
			const contractorWO = getContractorWO(workOrder, isClientView);
			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			const supplierFacilityId = isInternalTech(currentUser)
				? nullSafeGet('supplierFacilityId', workOrder)
				: values.supplierFacilityId || nullSafeGet('facility.id', currentUser);

			const invoice = buildInvoicePayloadFromFormValues(formValues, scope, {
				workOrderId,
				currentUser,
				role: 'supplier',
				baseMeta: {
					invoicePDFLink: currentInvoicePDFLink,
					isDeleted: invoiceFormData && invoiceFormData.isDeleted,
					buyerFacilityId: formValues.buyerFacilityId || nullSafeGet('facility.id', currentUser),
					supplierFacilityId,
				},
				status: isInternalTech(currentUser) ? 'pending' : 'draft',
			});

			if (formValues.signatureName) {
				invoice.aiParseMismatchSignedByEmail = currentUser.email;
			}

			// Echoed back exactly as the extraction sent it — the backend stores what the client
			// returns and deliberately does not re-derive it. It is what the invoice detail page
			// later reads to offer these values against the work order's asset record.
			//
			// Known gap: omitting the key makes the backend keep whatever it already stored, and it
			// accepts no explicit clear, so re-uploading a PDF that yields no suggestion leaves the
			// previous one on the invoice. Needs a backend change to fix.
			if (assetSuggestion) {
				invoice.assetSuggestion = assetSuggestion;
			}

			try {
				const finalInvoice = isUpdate ? { ...invoiceFormData, ...invoice } : invoice;

				if (parsedFormDataSnapshot) {
					const changeMetrics = compareParsedWithFinal(parsedFormDataSnapshot, finalInvoice);
					const hadEdits =
						nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
						nullSafeGet('total_line_items_changed', changeMetrics) > 0;

					if (isUpdate) {
						posthog.capture('invoice_updated_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					} else {
						posthog.capture('invoice_created_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					}
					finalInvoice.isAiParsed = true;
					finalInvoice.isAiParsedEdited = hadEdits;
				}
				if (contractorWO && isSubContractor(currentUser, workOrder)) {
					const record = await submitContractorAction({
						...finalInvoice,
						workOrderId: undefined,
						supplierFacilityId: undefined,
						contractorWorkOrderId: contractorWO.id,
						contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
						subSupplierFacilityId: contractorWO.subSupplierFacilityId,
					});
					if (onSuccess) {
						onSuccess(record);
					}
				} else {
					const record = await submitAction(finalInvoice);
					if (onSuccess) {
						onSuccess(record);
					}
				}
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			updateInvoice,
			createInvoice,
			invoiceFormData,
			currentUser,
			onSuccess,
			history,
			parsedFormDataSnapshot,
			assetSuggestion,
			userType,
			workOrderId,
			updateContractorInvoice,
			createContractorInvoice,
			isClientView,
			workOrder,
		]
	);

	const formConfig = useMemo(() => {
		if (!invoiceFormData || !workOrder || !currentUser) {
			return null;
		}

		return createSupplierInvoiceConfig(
			invoiceFormData,
			workOrder,
			currentUser,
			history,
			handleSubmit,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				parsedDataSnapshot: parsedFormDataSnapshot,
				workOrderCheck: nullSafeGet('workOrderCheck', acknowledgedWorkOrderCheck),
				rawParsedData: nullSafeGet('parsedData', acknowledgedWorkOrderCheck),
				disableCosts: disableCosts,
				onReset: handleReset,
				fetchTaxLineItems,
				currency,
				checkInvoiceNumber,
				isClientView,
			}
		);
	}, [
		invoiceFormData,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		acknowledgedWorkOrderCheck,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		disableCosts,
		handleReset,
		fetchTaxLineItems,
		currency,
		checkInvoiceNumber,
		isClientView,
	]);

	if (invoiceFormDataLoading || !formConfig) {
		return <PageLoadingPlaceholder />;
	}
	return (
		<Content>
			<BillToInfoBanner
				location={nullSafeGet('location', workOrder)}
				workOrder={workOrder}
				userType={userType}
				companyConfig={companyConfig}
				currentUser={currentUser}
			/>

			<InvoiceUploadandParse
				key={uploaderKey}
				// Withheld when the flag is off: the backend only runs the work order check for an
				// upload that names one, so this single omission disables the whole feature.
				workOrderId={isInvoiceWorkOrderCheckEnabled() ? workOrderId : undefined}
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={handleParsingStart}
				onParsingComplete={handleParsingComplete}
				onS3UrlReady={handleS3UrlReady}
				onS3UploadError={handleS3UploadError}
				uploaderType="dragger"
				onError={handleUploadError}
			/>
			<WorkOrderCheckModal
				visible={!!pendingWorkOrderCheck}
				workOrderCheck={nullSafeGet('workOrderCheck', pendingWorkOrderCheck)}
				parsedData={nullSafeGet('parsedData', pendingWorkOrderCheck)}
				workOrder={workOrder}
				onContinue={handleWorkOrderCheckContinue}
				onCancel={handleWorkOrderCheckCancel}
			/>
			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				// headerStyle={{ display: 'none' }}
				closeIcon={
					<Popconfirm
						title="Are you sure you want to go back? Any unsaved changes will be lost."
						onConfirm={handleBackConfirm}
						okText="Yes"
						cancelText="No"
					>
						<Button type="ghost" icon={<ArrowLeftOutlined />}>
							Back
						</Button>
					</Popconfirm>
				}
				title={<div className="h3medium">Create Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfUrl || null}
										fileName="Invoice.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							}
							rightPanel={
								<div>
									{parsingLoading ? (
										<InvoiceLoadingSkeletonAnimated />
									) : (
										<InvoiceFormBase
											key={formKey}
											config={formConfig}
											handleCancel={handleDeletePdf}
										/>
									)}
								</div>
							}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
	isClientView: state.clientView.isClientView,
	companyConfig: state.company_config.detail,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	checkInvoiceNumber: (invoice, supplierFacilityId) =>
		dispatch(getInvoiceNumber(invoice, supplierFacilityId)),
	createInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.create(invoice)),
	updateInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.update(invoice)),
	fetchTaxLineItems: (currencyId) => dispatch(fetchTaxLineItemsForSupplier(currencyId)),
	createContractorInvoice: (invoice) => dispatch(createInvoiceSubContractor(invoice)),
	updateContractorInvoice: (invoice) => dispatch(updateInvoiceSubContractor(invoice)),
	getQuotes: (params, targetCollectionName) =>
		dispatch(proposalsRestCrudThunksForSupplier.read(params, targetCollectionName)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(SupplierInvoiceAIForm))
);
